import React, { useCallback, useEffect, useMemo, useRef, createContext, useContext } from 'react';
import { usePermissionStore } from '@/stores/permissionStore';
import { useShallow } from 'zustand/react/shallow';
import { useModel } from '@umijs/max';
import { getAccountRoles, getProjectedPrimaryRoleId, isAccountAdmin } from '@/utils/accountRoles';

export interface DynamicPermissionProviderProps {
  children: React.ReactNode;
}

interface PermissionContextValue {
  hasPermission: (code: string) => boolean;
  getPermissionScope: (code: string) => 'all' | 'team' | 'self' | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  prefetchForRoute: (route: string) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

export const useDynamicPermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('useDynamicPermissions must be used within DynamicPermissionProvider');
  }
  return context;
};

/** Keep route checks and legacy profile consumers aligned with the live server projection. */
export const PermissionSessionBridge: React.FC = () => {
  const { initialState, setInitialState, refresh } = useModel('@@initialState');
  useEffect(() => {
    const resetSession = (event: StorageEvent) => {
      if (event.key !== 'token' || event.oldValue === event.newValue) return;
      (window as any).__FG_INSYS_CURRENT_USER__ = null;
      (window as any).__FG_INSYS_PERMISSIONS__ = [];
      void setInitialState((state) => ({ ...state, currentUser: undefined, permissions: [] }));
      if (event.newValue) {
        const expectedToken = event.newValue;
        void refresh()
          .then(async () => {
            if (localStorage.getItem('token') === expectedToken) {
              await usePermissionStore.getState().initialize();
            }
          })
          .catch(() => {
            // Keep the cleared view until authenticated loading can be retried.
          });
      }
    };
    window.addEventListener('storage', resetSession);
    return () => window.removeEventListener('storage', resetSession);
  }, [refresh, setInitialState]);
  const projection = usePermissionStore(
    useShallow((state) => ({
      userId: state.userId,
      permissions: state.userPermissions,
      roleAssignments: state.roleAssignments,
      rolesVersion: state.rolesVersion,
      fetchedAt: state.lastLivePermissionFetch,
    })),
  );
  useEffect(() => {
    const currentUser = initialState?.currentUser;
    if (!projection.fetchedAt || !currentUser || String(currentUser.id) !== projection.userId)
      return;
    const previousRoles = getAccountRoles(currentUser);
    const roles = projection.roleAssignments.map((assignment) => ({
      department: previousRoles.find((role) => String(role.id) === assignment.roleId)?.department,
      id: assignment.roleId,
      name: assignment.roleName,
    }));
    const roleIds = roles.map((role) => String(role.id)).sort();
    const permissions = projection.permissions.map((permission) => permission.code).sort();
    const primaryId = getProjectedPrimaryRoleId(
      projection.roleAssignments,
      roleIds,
      currentUser.role_id ?? currentUser.role?.id,
    );
    const nextUser = {
      ...currentUser,
      roles,
      role_ids: roleIds,
      role_id: primaryId,
      role: roles.find((role) => role.id === primaryId),
      access: isAccountAdmin({ roles }) ? 'admin' : 'user',
      roles_version: projection.rolesVersion,
    };
    const signature = JSON.stringify([
      primaryId,
      roleIds,
      roles.map((role) => role.name),
      permissions,
      projection.rolesVersion,
    ]);
    const previousSignature = JSON.stringify([
      currentUser.role_id ?? currentUser.role?.id,
      getAccountRoles(currentUser)
        .map((role) => String(role.id))
        .sort(),
      getAccountRoles(currentUser).map((role) => role.name),
      [...(initialState?.permissions ?? [])].sort(),
      currentUser.roles_version,
    ]);
    if (signature === previousSignature) return;
    (window as any).__FG_INSYS_CURRENT_USER__ = nextUser;
    (window as any).__FG_INSYS_PERMISSIONS__ = permissions;
    void setInitialState((state) =>
      String(state?.currentUser?.id) === projection.userId
        ? { ...state, currentUser: nextUser, permissions }
        : state,
    );
  }, [initialState, projection, setInitialState]);
  return null;
};

/**
 * Dynamic permission provider that loads permissions on mount
 * and provides them to child components via context
 */
export const DynamicPermissionProvider: React.FC<DynamicPermissionProviderProps> = ({
  children,
}) => {
  const {
    hasPermission,
    getPermissionScope,
    isLoadingGroups,
    isLoadingUserPermissions,
    groupsError,
    userPermissionsError,
    loadPermissionGroups,
    loadUserPermissions,
    clearCache,
    is401Handling,
    isInitialized,
  } = usePermissionStore(
    useShallow((state) => ({
      hasPermission: state.hasPermission,
      getPermissionScope: state.getPermissionScope,
      isLoadingGroups: state.isLoadingGroups,
      isLoadingUserPermissions: state.isLoadingUserPermissions,
      groupsError: state.groupsError,
      userPermissionsError: state.userPermissionsError,
      loadPermissionGroups: state.loadPermissionGroups,
      loadUserPermissions: state.loadUserPermissions,
      clearCache: state.clearCache,
      is401Handling: state.is401Handling,
      isInitialized: state.isInitialized,
    })),
  );

  const prefetchForRoute = useCallback(async (route: string) => {
    const { permissionPrefetchService } = await import('@/services/permission/prefetchService');
    await permissionPrefetchService.prefetchForRoute(route);
  }, []);
  const initStartedRef = useRef(false);
  const lastFocusTimeRef = useRef(0);

  const isLoading = isLoadingGroups || isLoadingUserPermissions;
  const error = groupsError || userPermissionsError;

  // Load permissions on mount with proper coordination
  useEffect(() => {
    // Prevent duplicate initialization
    if (initStartedRef.current || isInitialized) {
      return;
    }

    initStartedRef.current = true;

    const loadData = async () => {
      try {
        // Check authentication before loading
        const { authManager, AuthSystem } = await import('@/services/auth/authManager');
        if (!authManager.isAuthenticated(AuthSystem.ADMIN)) {
          console.debug(
            '[DynamicPermissionProvider] Skipping permission load - user not authenticated',
          );
          return;
        }

        // Check if we're not in 401 handling mode
        if (is401Handling) {
          console.debug(
            '[DynamicPermissionProvider] Skipping permission load - 401 handling in progress',
          );
          return;
        }

        console.log('[DynamicPermissionProvider] Starting permission initialization');

        // Use the permission store's initialize method directly (handles caching)
        const permissionStore = usePermissionStore.getState();
        await permissionStore.initialize();

        console.log('[DynamicPermissionProvider] Permission initialization completed');

        // Initialize prefetch service after successful load (NO WebSocket for permissions)
        // Only if we're still authenticated after initialization
        if (authManager.isAuthenticated(AuthSystem.ADMIN)) {
          try {
            const { permissionPrefetchService } = await import(
              '@/services/permission/prefetchService'
            );

            permissionPrefetchService.warmUpCache();
            console.log('[DynamicPermissionProvider] Prefetch service initialized');
          } catch (prefetchError) {
            console.warn(
              '[DynamicPermissionProvider] Failed to initialize prefetch service, continuing without it:',
              prefetchError,
            );
          }
        }
      } catch (err) {
        console.error('Failed to load permissions:', err);

        // Try to use temporary data as fallback (only for page refresh scenarios)
        const permissionStore = usePermissionStore.getState();
        const hasTempData = await permissionStore.loadOfflineData();

        if (hasTempData) {
          console.log(
            '[DynamicPermissionProvider] Using temporary permissions as fallback (page refresh scenario)',
          );
          // Don't show error if we have temporary data for page refresh
          return;
        }
      }
    };

    // Execute load immediately without delay
    loadData();
  }, [is401Handling, isInitialized]); // Empty deps to ensure single initialization

  // Refresh user permissions when window gains focus (with throttling)
  useEffect(() => {
    const handleFocus = async () => {
      const now = Date.now();
      if (now - lastFocusTimeRef.current < 600000) return; // Throttle to once per 10 minutes
      lastFocusTimeRef.current = now;

      const { authManager, AuthSystem } = await import('@/services/auth/authManager');
      if (authManager.isAuthenticated(AuthSystem.ADMIN) && !is401Handling && isInitialized) {
        // Only refresh if we're initialized and not in error state
        if (!isLoadingUserPermissions && !userPermissionsError) {
          loadUserPermissions().catch(console.error);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [
    is401Handling,
    isInitialized,
    isLoadingUserPermissions,
    userPermissionsError,
    loadUserPermissions,
  ]);

  // Refresh function
  const refresh = useCallback(async () => {
    clearCache();
    await Promise.all([loadPermissionGroups(), loadUserPermissions()]);
  }, [clearCache, loadPermissionGroups, loadUserPermissions]);

  const contextValue = useMemo<PermissionContextValue>(
    () => ({
      hasPermission,
      getPermissionScope,
      isLoading,
      error,
      refresh,
      prefetchForRoute,
    }),
    [error, getPermissionScope, hasPermission, isLoading, prefetchForRoute, refresh],
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      <PermissionSessionBridge />
      {children}
    </PermissionContext.Provider>
  );
};
